import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchProduct } from "../store/features/productsSlice";
import { addToCart } from "../store/features/cartSlice";
import ProductGallery from "../components/ProductGallery";
import { ArrowLeftIcon, CheckCircleIcon, ShoppingCartIcon } from "@heroicons/react/24/outline";
import { Button, Loader } from "../components/ui";
import { trackEvent } from "../analytics";

const ProductPage = () => {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { status } = useSelector((state) => state.products);
  const product = useSelector((state) => state.products.currentProduct);

  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    // Сторінка товару — з початку (інакше відкривається на висоті, де був каталог)
    window.scrollTo(0, 0);
    dispatch(fetchProduct(slug));
  }, [dispatch, slug]);

  // Перегляд товару — коли завантажився саме цей товар (а не попередній зі стора)
  const viewedId = product?.slug === slug ? product.id : null;
  useEffect(() => {
    if (viewedId) trackEvent("ViewContent", { product_id: viewedId });
  }, [viewedId]);

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setShowToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  if (status === "loading" || !product) {
    return <Loader className="min-h-[50vh]" />;
  }

  const galleryImages = [{ image: product.image }];

  const handleAddToCart = () => {
    dispatch(addToCart(product));
    setShowToast(true);
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden pb-24">

      {/* PRODUCT IMAGE */}
      <div className="px-4 py-2">
        <div className="relative aspect-square w-full overflow-hidden rounded-3xl bg-slate-800 border border-slate-700 flex items-center justify-center">

          <ProductGallery images={galleryImages} />

        </div>
      </div>

      {/* PRODUCT INFO */}
      <div className="px-4 pt-6">

        <div className="flex items-start justify-between">

          <div>

            <h1 className="text-3xl font-extrabold leading-tight mt-1">
              {product.name}
            </h1>
          </div>

          <div className="text-right">

            <p className="text-2xl font-bold text-blue-400 whitespace-nowrap">
              {product.price} zł
            </p>

            {product.old_price && (
              <p className="text-xs text-slate-500 line-through">
                {product.old_price} zł
              </p>
            )}

          </div>

        </div>

        {/* CHARACTERISTICS */}
        {product.characteristics && (
          <div className="mt-8 space-y-4">

            <h3 className="text-lg font-bold">
              Характеристики
            </h3>

            <p className="text-slate-300 leading-relaxed whitespace-pre-line">
              {product.characteristics}
            </p>

          </div>
        )}

        {/* DESCRIPTION */}
        {product.description && (
          <div className="mt-8 space-y-4">

            <h3 className="text-lg font-bold">
              Опис товару
            </h3>

            <p className="text-slate-300 leading-relaxed">
              {product.description}
            </p>

          </div>
        )}

      </div>

      {/* STICKY ADD TO CART */}

      <div className="fixed bottom-0 left-0 right-0 p-4 pb-[calc(env(safe-area-inset-bottom)+16px)] bg-[#101622]/95 backdrop-blur-xl border-t border-slate-700 flex items-center gap-3 z-50">
        <Button variant="secondary" size="icon" aria-label="Назад" onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="w-5 h-5" />
        </Button>

        <Button size="lg" className="flex-1 !rounded-full" onClick={handleAddToCart}>
          <ShoppingCartIcon className="w-6 h-6" />
          Добавить в корзину
        </Button>
      </div>

      {/* TOAST NOTIFICATION */}
      {showToast && (
        <div
          role="status"
          className="fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 whitespace-nowrap bg-emerald-500 text-white font-semibold px-5 py-3 rounded-2xl shadow-lg shadow-emerald-500/30"
        >
          <CheckCircleIcon className="w-5 h-5" />
          Товар добавлен в корзину!
        </div>
      )}

    </div>
  );
};

export default ProductPage;